import { apiService } from './apiService';
import ToastMe from '@/components/ui/ToastMe';

/* tokens */
function getCustomerAuthHeaders() {
  const token =
    localStorage.getItem('customer_jwt') ?? localStorage.getItem('customerToken');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}
function getAdminAuthHeaders() {
  const token = localStorage.getItem('jwt_token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

/* ───────────────── Password / Profile (unchanged) ───────────────── */
export function changeCustomerPasswordService({ old_password, password, customerIdForAdminFallback } = {}) {
  const customerToken =
    localStorage.getItem('customer_jwt') ?? localStorage.getItem('customerToken');

  if (customerToken) {
    return apiService({
      method: 'PUT',
      url: '/customer/me/change-password',
      data: { old_password, password },
      headers: { Authorization: `Bearer ${customerToken}` },
      onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Password changed', 'success'),
    });
  }

  const adminToken = localStorage.getItem('jwt_token');
  if (adminToken && customerIdForAdminFallback) {
    return apiService({
      method: 'PUT',
      url: `/customer/${customerIdForAdminFallback}/change-password`,
      data: { password },
      headers: { Authorization: `Bearer ${adminToken}` },
      onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Password reset by admin', 'success'),
    });
  }

  return Promise.reject({
    statusCode: 401,
    message:
      'You must be logged in as this customer to change your own password, or provide an admin token + customerId to reset.',
  });
}

export function getCustomerProfileService() {
  const headers = getCustomerAuthHeaders();
  return apiService({ method: 'GET', url: '/customer/my-profile', headers });
}

export function updateCustomerProfileService(data) {
  const headers = getCustomerAuthHeaders();
  return apiService({
    method: 'PUT',
    url: '/customer/my-profile',
    data,
    headers,
    onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Profile updated successfully', 'success'),
  });
}

/* ───────────────── CRUD (ADMIN ROUTES) ───────────────── */
export function getCustomerService(data) {
  const headers = getAdminAuthHeaders(); // admin → /customer/data
  return apiService({ method: 'POST', url: '/customer/data', data, headers });
}
export function getByIdCustomerService(id) {
  const headers = getAdminAuthHeaders(); // admin or customer; admin for panel
  return apiService({ method: 'GET', url: `/customer/${id}`, headers });
}
export function addCustomerService(data) {
  const headers = getAdminAuthHeaders();
  return apiService({
    method: 'POST',
    url: '/customer',
    data,
    headers,
    onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Customer created', 'success'),
  });
}
export function updateCustomerService(id, data) {
  const headers = getAdminAuthHeaders();
  return apiService({
    method: 'PUT',
    url: `/customer/${id}`,
    data,
    headers,
    onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Customer updated', 'success'),
  });
}
export function deleteCustomerService(id) {
  const headers = getAdminAuthHeaders();
  return apiService({
    method: 'DELETE',
    url: `/customer/${id}`,
    headers,
    onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Customer deleted', 'success'),
  });
}

/* ───────────────── Photos (admin + customer) ───────────────── */
export function updateCustomerPhotoService(id, formData) {
  const headers = getAdminAuthHeaders();
  return apiService({
    method: 'PUT',
    url: `/customer/${id}/photo`,
    data: formData,
    headers,
    onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Photo updated', 'success'),
  });
}
export function updateMyProfilePhotoService(formData) {
  const headers = getCustomerAuthHeaders();
  return apiService({
    method: 'PUT',
    url: '/customer/my-profile/photo',
    data: formData,
    headers,
    onSuccess: (_d, res) => ToastMe(res?.data?.message || 'Photo updated', 'success'),
  });
}

/* ───────────────── Credit (customer-first; keep as-is) ───────────────── */
export function getCustomerCreditService({ customerId }) {
  const headers = getCustomerAuthHeaders();
  return apiService({ method: 'GET', url: `/customer/${customerId}/credit`, headers });
}
export function payCustomerCreditService({ customerId, amount }) {
  const headers = getCustomerAuthHeaders();
  return apiService({
    method: 'POST',
    url: `/customer/${customerId}/credit/pay`,
    data: { amount },
    headers,
    onSuccess: (_d, res) => ToastMe(res?.data?.message),
  });
}
export function getCustomerCreditItemsService({ customerId, status }) {
  const headers = getCustomerAuthHeaders();
  return apiService({
    method: 'GET',
    url: `/customer/${customerId}/credits${status ? `?status=${status}` : ''}`,
    headers,
  });
}
export function getCreditReportService({ customerId, page = 1, limit = 10 }) {
  const headers = getCustomerAuthHeaders();
  return apiService({
    method: 'GET',
    url: `/customer/${customerId}/credit-report`,
    headers,
    params: { page, limit },
  });
}
export function getCreditReportAllService({ page = 1, limit = 10 }) {
  const headers = getAdminAuthHeaders();
  return apiService({
    method: 'GET',
    url: '/customer/credit-report-all',
    headers,
    params: { page, limit },
  });
}
export function getCreditBalanceReportAllService({ page = 1, limit = 10, search = '' }) {
  const headers = getAdminAuthHeaders();
  return apiService({
    method: 'GET',
    url: '/customer/credit-balance-list',
    headers,
    params: { page, limit, search },
  });
}

/* ───────────────── Orders (customer-first + admin fallback) ───────────────── */
export const getCustomerOrdersService = ({ customerId, limit = 50 }) => {
  return (dispatch) => {
    const tryCustomer = () =>
      dispatch(
        apiService({
          method: 'GET',
          url: `/customer/${customerId}/orders`,
          data: { limit },
          headers: getCustomerAuthHeaders(),
        }),
      );

    const tryAdminFallback = () => {
      const headers = getAdminAuthHeaders();
      if (!headers) return Promise.reject({ statusCode: 401, message: 'No admin token for fallback' });
      return dispatch(
        apiService({
          method: 'POST',
          url: '/order/recent',
          data: { customerId, limit },
          headers,
        }),
      );
    };

    return tryCustomer().catch((err) => {
      if ([401, 404, 405].includes(err?.statusCode)) return tryAdminFallback();
      throw err;
    });
  };
};
